import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import { getNewHireForGuestLogin } from '../../services/hireService';
import { setNewHireGuestSession } from '../../services/hireService';
import TrademarkFooter from '../common/TrademarkFooter';

/**
 * Passwordless login for new hires only.
 * User enters email; we check profiles (role = new_hire, team_member_email = email)
 * and then take them to /onboarding without any auth.
 */
const NewHireLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const hire = await getNewHireForGuestLogin(email);

      if (!hire) {
        setError('Email not found. If you’re a new hire, make sure you’re using the email we have on file.');
        return;
      }

      setNewHireGuestSession(email, hire.id);
      navigate('/onboarding', { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Something went wrong';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Typography component="h1" variant="h5" gutterBottom>
            New Hire Login
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
            Enter your email to access onboarding. No password needed.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email address"
              name="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? 'Checking…' : 'Continue to onboarding'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <MuiLink
                component={RouterLink}
                to="/admin-login"
                variant="body2"
                sx={{ color: 'primary.main', fontWeight: 500 }}
              >
                Admin login
              </MuiLink>
            </Box>
          </Box>
        </Paper>
      </Box>
      <TrademarkFooter />
    </Container>
  );
};

export default NewHireLoginPage;
